import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { DateTime } from 'luxon'
import {
  faUser,
  faPause,
  faBuilding,
  faEyeSlash,
  faCirclePause,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons'

import {
  selectPlaidItems,
  selectVisibleFinancialAccounts,
} from '@/selectors/financeSelectors'
import {
  fetchFinancialAccountsIfNeeded,
  FETCH_FINANCIAL_ACCOUNT_KEY,
} from '@/actions/financialAccountActions'
import {
  fetchPlaidItemsIfNeeded,
  FETCH_PLAID_ITEM_KEY,
} from '@/actions/plaidItemActions'
import { SyncErrorClassification } from '@/actions/plaid/authActions'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import {
  type ActivateAs,
  FinancialAccount,
  FinancialType,
} from '@/reducers/finances/financialAccountsReducer'
import {
  PlaidItem,
  type PlaidItemState,
} from '@/reducers/finances/plaidItemReducer'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'

import {
  Accordion,
  Button,
  Card,
  Icon,
  Text,
  Skeleton,
  VerticalThreeDotMenu,
  Pill,
  type VerticalThreeDotMenuOption,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import { AccountInstitutionLogo } from './AccountInstitutionLogo'
import { ConnectNewAccountMenu } from './ConnectNewAccountMenu'
import { DeactivateAccountModal } from './DeactivateAccountModal'
import { ReactivateAccountModal } from './ReactivateAccountModal'
import { NoBankAccountsBanner } from './NoBankAccountsBanner'
import { useSyncStatusContext } from './SyncStatusContext'
import {
  AccountFilters,
  applyAccountFilters,
  EMPTY_ACCOUNT_FILTERS,
  type AccountFilterState,
} from './AccountFilters'
import { SYNC_ERROR_MESSAGES } from '@/components/plaid/syncErrorMessages'
import { SHOW_ACCOUNT_ACTIONS, sortAccounts } from './bankAccounts.helpers'
import { usePlaidRecovery } from './usePlaidRecovery'

interface BankAccountCardProps {
  account: FinancialAccount
  institution: PlaidItem | undefined
  errorClassification: SyncErrorClassification | null
}

const AccountBalance = ({ account }: { account: FinancialAccount }) => (
  <div className="flex flex-col items-end">
    {account.currentBalanceInCents !== null && (
      <>
        <Text variant={TypographyStyles.BODY_LG}>
          {formatCurrencyFromCents(account.currentBalanceInCents)}
        </Text>
        {account.balanceEffectiveDate && (
          <Text variant={TypographyStyles.BODY_XS}>
            {DateTime.fromISO(account.balanceEffectiveDate).toRelative()}
          </Text>
        )}
      </>
    )}
  </div>
)

const AccountLabels = ({ account }: { account: FinancialAccount }) => (
  <div className="flex items-center gap-2">
    {account.markedPersonalAt !== null && (
      <Pill icon={faUser} uppercase={false} size="sm" className="bg-yellow-25">
        Personal
      </Pill>
    )}
    {account.inactive && (
      <Pill color="gray" icon={faPause} uppercase={false} size="sm">
        Inactive
      </Pill>
    )}
    {account.accountType === FinancialType.MANUAL && (
      <Pill color="gray" uppercase={false} size="sm">
        Manual
      </Pill>
    )}
  </div>
)

/**
 * Replaces the balance / three-dot menu on the right of a failing account card with a
 * red warning icon and the recovery action for the item's sync-error classification —
 * Reconnect (page-level `PlaidLauncher`) or Retry (re-sync), same actions as the
 * page-level `FinancialInstitutionsError` banner.
 */
const AccountSyncErrorAction = ({
  account,
  classification,
}: {
  account: FinancialAccount
  classification: SyncErrorClassification
}) => {
  const { reconnect, retry } = usePlaidRecovery()
  const { action } = SYNC_ERROR_MESSAGES[classification]
  const { plaidItemId } = account

  return (
    <div className="flex items-center gap-2 text-red-200">
      <Icon icon={faTriangleExclamation} color="text-red-200" size="sm" />
      {action === 'reconnect' && (
        <Button
          variant="link"
          className="p-0 h-auto text-red-200"
          onClick={() => reconnect(account.id)}
        >
          Reconnect
        </Button>
      )}
      {action === 'retry' && plaidItemId && (
        <Button
          variant="link"
          className="p-0 h-auto text-red-200"
          onClick={() => retry(plaidItemId)}
        >
          Retry
        </Button>
      )}
    </div>
  )
}

const BankAccountCardSkeleton = () => (
  <Card className="my-0" childrenClassName="p-6">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <Skeleton className="size-12 rounded-lg shrink-0" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <Skeleton className="h-4 w-20" />
    </div>
  </Card>
)

const ACTIVE_ACCOUNT_OPTIONS: VerticalThreeDotMenuOption[] = [
  { key: 'deactivate', label: 'Deactivate', icon: faCirclePause },
]

const INACTIVE_ACCOUNT_OPTIONS: VerticalThreeDotMenuOption[] = [
  { key: 'reactivate', label: 'Reactivate', icon: faBuilding },
]

const INACTIVE_PERSONAL_ACCOUNT_OPTIONS: VerticalThreeDotMenuOption[] = [
  { key: 'reactivate', label: 'Reactivate', icon: faUser },
  {
    key: 'reactivateBusiness',
    label: 'Reactivate as business account',
    icon: faBuilding,
  },
]

const PERSONAL_SECTION = 'personal-and-inactive'

const isActiveBusinessAccount = (account: FinancialAccount) =>
  !account.inactive && account.markedPersonalAt === null

const PersonalAccountsSection = ({
  count,
  defaultExpanded = false,
  children,
}: {
  count: number
  defaultExpanded?: boolean
  children: ReactNode
}) => (
  <div className="mt-4">
    <Accordion
      variant="text"
      type="single"
      collapsible
      defaultValue={defaultExpanded ? PERSONAL_SECTION : undefined}
    >
      <Accordion.Item value={PERSONAL_SECTION}>
        <Accordion.Trigger className="w-fit justify-start gap-2 no-underline hover:no-underline">
          <Icon icon={faEyeSlash} size="sm" />
          <Text variant={TypographyStyles.BODY_SM} className="font-medium">
            {SHOW_ACCOUNT_ACTIONS
              ? 'Personal and inactive accounts'
              : 'Personal accounts'}{' '}
            ({count})
          </Text>
        </Accordion.Trigger>
        <Accordion.Content>
          <div className="flex flex-col gap-2">{children}</div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion>
  </div>
)

type AccountModal =
  | { type: 'deactivate' }
  | { type: 'reactivate'; activateAs: ActivateAs }

const AccountModalRouter = ({
  modal,
  accountId,
  onClose,
}: {
  modal: AccountModal | null
  accountId: number
  onClose: () => void
}) => {
  switch (modal?.type) {
    case 'deactivate':
      return <DeactivateAccountModal accountId={accountId} onClose={onClose} />
    case 'reactivate':
      return (
        <ReactivateAccountModal
          accountId={accountId}
          activateAs={modal.activateAs}
          onClose={onClose}
        />
      )
    default:
      return null
  }
}

const AccountCards = ({
  accounts,
  plaidItems,
  errorByPlaidItemId,
}: {
  accounts: FinancialAccount[]
  plaidItems: PlaidItemState | undefined
  errorByPlaidItemId: ReadonlyMap<number, SyncErrorClassification>
}) => (
  <>
    {accounts.map((account) => (
      <BankAccountCard
        key={account.id}
        account={account}
        institution={
          account.plaidItemId ? plaidItems?.[account.plaidItemId] : undefined
        }
        errorClassification={
          account.plaidItemId
            ? (errorByPlaidItemId.get(account.plaidItemId) ?? null)
            : null
        }
      />
    ))}
  </>
)

/**
 * Shown when nothing is connected for business use.
 */
const NoActiveAccountsView = ({
  personalAccounts,
  plaidItems,
  errorByPlaidItemId,
}: {
  personalAccounts: FinancialAccount[]
  plaidItems: PlaidItemState | undefined
  errorByPlaidItemId: ReadonlyMap<number, SyncErrorClassification>
}) => (
  <div className="flex flex-col gap-6">
    <NoBankAccountsBanner />
    {personalAccounts.length > 0 && (
      <PersonalAccountsSection count={personalAccounts.length} defaultExpanded>
        <AccountCards
          accounts={personalAccounts}
          plaidItems={plaidItems}
          errorByPlaidItemId={errorByPlaidItemId}
        />
      </PersonalAccountsSection>
    )}
  </div>
)

const BankAccountCard = ({
  account,
  institution,
  errorClassification,
}: BankAccountCardProps) => {
  const [modal, setModal] = useState<AccountModal | null>(null)

  const showBalance =
    account.markedPersonalAt === null &&
    !account.inactive &&
    account.accountType !== FinancialType.MANUAL
  const menuOptions = (() => {
    if (!account.inactive) return ACTIVE_ACCOUNT_OPTIONS
    // Manual accounts don't sync, so they are never reactivated as business.
    if (
      account.markedPersonalAt !== null &&
      account.accountType !== FinancialType.MANUAL
    )
      return INACTIVE_PERSONAL_ACCOUNT_OPTIONS
    return INACTIVE_ACCOUNT_OPTIONS
  })()

  const handleMenuSelect = (option: VerticalThreeDotMenuOption) => {
    if (option.key === 'deactivate') setModal({ type: 'deactivate' })
    // "Reactivate" keeps the account's current categorization; only the explicit
    // "as business account" option converts a personal account.
    if (option.key === 'reactivate')
      setModal({
        type: 'reactivate',
        activateAs: account.markedPersonalAt !== null ? 'personal' : 'business',
      })
    if (option.key === 'reactivateBusiness')
      setModal({ type: 'reactivate', activateAs: 'business' })
  }

  return (
    <>
      <Card className="my-0" childrenClassName="p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <AccountInstitutionLogo
              institution={institution}
              institutionName={account.plaidInstitutionName ?? undefined}
            />
            <div className="flex flex-col">
              <Text variant={TypographyStyles.BODY_SM} className="font-medium">
                {account.plaidInstitutionName} {account.subtype}
              </Text>
              <div className="flex items-center gap-2">
                <Text
                  variant={TypographyStyles.BODY_XS}
                  className="font-medium"
                >
                  {account.type}
                </Text>
                <Text
                  variant={TypographyStyles.BODY_XS}
                  className="font-normal"
                >
                  ••••{account.mask}
                </Text>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            {errorClassification ? (
              <AccountSyncErrorAction
                account={account}
                classification={errorClassification}
              />
            ) : (
              <>
                {showBalance ? (
                  <AccountBalance account={account} />
                ) : (
                  <AccountLabels account={account} />
                )}
                {SHOW_ACCOUNT_ACTIONS && (
                  <VerticalThreeDotMenu
                    options={menuOptions}
                    onSelect={handleMenuSelect}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </Card>
      <AccountModalRouter
        modal={modal}
        accountId={account.id}
        onClose={() => setModal(null)}
      />
    </>
  )
}

export const BankAccountsList = () => {
  const dispatch = useAppDispatch()
  const allAccounts = useReselector(selectVisibleFinancialAccounts)
  const plaidItems = useReselector(selectPlaidItems)
  const isLoadingAccounts = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_FINANCIAL_ACCOUNT_KEY
  )
  const isLoadingPlaidItems = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_PLAID_ITEM_KEY
  )
  const [filters, setFilters] = useState<AccountFilterState>(
    EMPTY_ACCOUNT_FILTERS
  )
  const { plaidItems: syncItems } = useSyncStatusContext()

  const errorByPlaidItemId = useMemo(() => {
    const map = new Map<number, SyncErrorClassification>()
    syncItems.forEach((item) => {
      if (item.resolvedErrorClassification) {
        map.set(item.plaidItemId, item.resolvedErrorClassification)
      }
    })
    return map
  }, [syncItems])

  const { businessAccountsList, personalAccountsList } = useMemo(
    () =>
      sortAccounts(
        applyAccountFilters(allAccounts, filters, errorByPlaidItemId),
        errorByPlaidItemId
      ),
    [allAccounts, filters, errorByPlaidItemId]
  )

  useEffect(() => {
    dispatch(fetchFinancialAccountsIfNeeded(true, { unreconciledCounts: true }))
    dispatch(fetchPlaidItemsIfNeeded())
  }, [dispatch])

  if (isLoadingAccounts || isLoadingPlaidItems) {
    return (
      <div className="flex flex-col gap-2">
        {[1, 2, 3].map((n) => (
          <BankAccountCardSkeleton key={n} />
        ))}
      </div>
    )
  }

  const noAccountsMatchFilters =
    businessAccountsList.length === 0 && personalAccountsList.length === 0

  const hasActiveBusinessAccount = allAccounts.some(isActiveBusinessAccount)

  if (!hasActiveBusinessAccount) {
    return (
      <NoActiveAccountsView
        personalAccounts={personalAccountsList}
        plaidItems={plaidItems}
        errorByPlaidItemId={errorByPlaidItemId}
      />
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between mb-2">
        <AccountFilters
          accounts={allAccounts}
          filters={filters}
          onChange={setFilters}
        />
        <ConnectNewAccountMenu />
      </div>
      {noAccountsMatchFilters && (
        <Text
          variant={TypographyStyles.BODY_SM}
          className="text-neutral-600 py-4"
        >
          No accounts match the selected filters.
        </Text>
      )}
      <AccountCards
        accounts={businessAccountsList}
        plaidItems={plaidItems}
        errorByPlaidItemId={errorByPlaidItemId}
      />
      {personalAccountsList.length > 0 && (
        <PersonalAccountsSection count={personalAccountsList.length}>
          <AccountCards
            accounts={personalAccountsList}
            plaidItems={plaidItems}
            errorByPlaidItemId={errorByPlaidItemId}
          />
        </PersonalAccountsSection>
      )}
    </div>
  )
}
